// @ts-check
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';

export default function Branches() {
  const [rows, setRows] = useState(/** @type {any[]} */ ([]));
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  async function load() {
    const r = await api('/admin/branches');
    setRows(r.branches);
  }
  useEffect(() => { load().catch((e) => setErr(e.message)); }, []);

  if (err) return <p className="text-red-400">{err}</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Branches</h1>
        <button className="btn-primary" onClick={() => setShowCreate((s) => !s)}>
          {showCreate ? 'Close' : '+ New branch'}
        </button>
      </div>

      {showCreate && (
        <CreateBranchForm
          onCreated={async () => { setShowCreate(false); await load(); }}
        />
      )}

      <div className="card">
        <table className="w-full">
          <thead className="text-left text-neutral-400 text-sm border-b border-neutral-800">
            <tr>
              <th className="py-2">Code</th>
              <th className="py-2">Name</th>
              <th className="py-2">GSTIN</th>
              <th className="py-2">Phone</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b._id} className="border-b border-neutral-900">
                <td className="py-2 font-mono">{b.code}</td>
                <td className="py-2">{b.name}</td>
                <td className="py-2 font-mono">{b.gstin ?? '—'}</td>
                <td className="py-2">{b.phone ?? '—'}</td>
                <td className="py-2 text-right">
                  <Link to={`/branches/${b._id}`} className="text-red-400 hover:underline">Open →</Link>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="py-4 text-neutral-500">No branches yet — create one above or sync from a POS terminal.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Crockford-style 26-char ULID, matching @katsura/shared/ulid so ids generated
// here line up with the desktop-side primary key shape and pass backend Zod
// (id.min(8)). Self-contained to avoid pulling the workspace dep into the SPA.
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
function ulid() {
  const time = Date.now();
  let timeStr = '';
  let t = time;
  for (let i = 0; i < 10; i++) {
    timeStr = CROCKFORD[t % 32] + timeStr;
    t = Math.floor(t / 32);
  }
  const rand = new Uint8Array(16);
  crypto.getRandomValues(rand);
  let randStr = '';
  for (let i = 0; i < 16; i++) randStr += CROCKFORD[rand[i] % 32];
  return timeStr + randStr;
}

// 32-hex-char enrolment secret (128-bit). Branch-side enrol flow checks this
// against the value stored on the Mongo branch doc to mint the sync token.
function randomSecret() {
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  return Array.from(buf).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** @param {{ onCreated: () => Promise<void> | void }} props */
function CreateBranchForm({ onCreated }) {
  const [form, setForm] = useState({
    name: '', code: '', gstin: '', state_code: '', address: '', phone: '', email: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [createdSecret, setCreatedSecret] = useState('');

  async function submit(/** @type {React.FormEvent} */ e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const id = ulid();
      const enrolment_secret = randomSecret();
      await api('/admin/branches', {
        method: 'POST',
        body: JSON.stringify({
          id,
          name: form.name,
          code: form.code,
          gstin: form.gstin || undefined,
          state_code: form.state_code || undefined,
          address: form.address || undefined,
          phone: form.phone || undefined,
          email: form.email || undefined,
          enrolment_secret,
        }),
      });
      // Surface the secret once so the operator can paste it into the desktop
      // enrol flow. Not retrievable later from the list (backend strips it).
      setCreatedSecret(enrolment_secret);
      setForm({ name: '', code: '', gstin: '', state_code: '', address: '', phone: '', email: '' });
      await onCreated();
    } catch (e) {
      setErr(/** @type {Error} */ (e).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card space-y-3 max-w-3xl">
      <h2 className="font-semibold">Create branch</h2>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
        <Field label="Code" value={form.code} onChange={(v) => setForm({ ...form, code: v })} required />
        <Field label="GSTIN" value={form.gstin} onChange={(v) => setForm({ ...form, gstin: v })} />
        <Field label="State code (e.g. 27)" value={form.state_code} onChange={(v) => setForm({ ...form, state_code: v })} />
        <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
      </div>
      <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
      {err && <p className="text-red-400 text-sm">{err}</p>}
      {createdSecret && (
        <div className="rounded border border-yellow-700 bg-yellow-950/40 p-3 text-sm">
          <p className="font-semibold text-yellow-300">Branch created. Save the enrolment secret — it isn’t shown again:</p>
          <code className="block break-all mt-1 text-yellow-200">{createdSecret}</code>
        </div>
      )}
      <button disabled={busy} className="btn-primary">{busy ? 'Creating…' : 'Create'}</button>
    </form>
  );
}

/** @param {{ label: string, value: string, onChange: (v: string) => void, required?: boolean }} props */
function Field({ label, value, onChange, required }) {
  return (
    <label className="block">
      <span className="text-sm text-neutral-400">{label}{required && ' *'}</span>
      <input className="input mt-1 w-full" value={value} onChange={(e) => onChange(e.target.value)} required={required} />
    </label>
  );
}
